import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Считывает высоту sticky/fixed header'а из CSS-переменной --header-height.
 * Возвращает 0, если переменная не задана или некорректна.
 */
function getHeaderOffset(): number {
  if (typeof window === 'undefined') {
    return 0;
  }
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--header-height')
    .trim();
  if (!raw) {
    return 0;
  }
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Пытается проскроллить к элементу с указанным id с учётом фиксированного header'а.
 * Возвращает true, если элемент найден.
 */
function scrollToHash(hash: string): boolean {
  const id = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!id) {
    return false;
  }
  const el = document.getElementById(id);
  if (!el) {
    return false;
  }

  const offset = getHeaderOffset();
  const rect = el.getBoundingClientRect();
  const targetY = rect.top + window.scrollY - offset;

  window.scrollTo({ top: targetY, left: 0, behavior: 'smooth' });
  return true;
}

export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Пробуем сразу: элемент уже в DOM (типичный случай для не-lazy маршрутов).
      if (scrollToHash(hash)) {
        return;
      }

      // Элемент ещё не смонтирован — даём React время на рендер.
      // Повторяем попытку через requestAnimationFrame, затем через MutationObserver
      // на короткий период (≤ 500мс) на случай lazy-загрузки/асинхронных эффектов.
      let cancelled = false;

      const rafId = window.requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }
        if (scrollToHash(hash)) {
          return;
        }

        const observer = new MutationObserver(() => {
          if (cancelled) {
            return;
          }
          if (scrollToHash(hash)) {
            observer.disconnect();
            window.clearTimeout(timeoutId);
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });

        const timeoutId = window.setTimeout(() => {
          observer.disconnect();
        }, 500);
      });

      return () => {
        cancelled = true;
        window.cancelAnimationFrame(rafId);
      };
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  return null;
}
